import React, { useState, useEffect } from 'react';
import { Button, Table, Badge, Spinner, Alert, Card } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import TemplateForm from './TemplateForm';

function TemplateManager({ onBack, onStartWorkout }) {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workoutAPI.fetchTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEdit = async (template) => {
    try {
      // Fetch full template details including exercises
      const fullTemplate = await workoutAPI.fetchTemplateById(template.id);
      setEditingTemplate(fullTemplate);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      setError('Failed to load template details. Please try again.');
    }
  };

  const handleSave = async (formData) => {
    setError(null);
    try {
      if (editingTemplate) {
        await workoutAPI.updateTemplate(editingTemplate.id, formData);
      } else {
        await workoutAPI.createTemplate(formData);
      }
      setShowForm(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please try again.');
    }
  };

  const handleDelete = async (templateId) => {
    if (!deleteConfirm || deleteConfirm !== templateId) {
      setDeleteConfirm(templateId);
      return;
    }

    setError(null);
    try {
      await workoutAPI.deleteTemplate(templateId);
      setDeleteConfirm(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  const handleStartFromTemplate = async (template) => {
    try {
      // Fetch full template details including exercises
      const fullTemplate = await workoutAPI.fetchTemplateById(template.id);
      onStartWorkout(fullTemplate);
    } catch (err) {
      console.error('Error loading template:', err);
      setError('Failed to load template. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading templates...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="template-manager">
      <div className="d-flex justify-content-between align-items-center mb-4 p-1">
        <div>
          <h2>
            <i className="fas fa-file-alt me-2"></i>
            Workout Templates
          </h2>
          <p className="text-muted">Create and manage your workout templates</p>
        </div>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="mb-4">
        <Button variant="primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>
          Create New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="fas fa-file-alt fa-4x text-muted mb-3"></i>
            <h4>No Templates Yet</h4>
            <p className="text-muted">
              Create your first workout template to save time on your recurring workouts
            </p>
            <Button variant="primary" onClick={handleCreateNew}>
              <i className="fas fa-plus me-2"></i>
              Create Template
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="templates-list">
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Exercises</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>
                    <strong>{template.name}</strong>
                  </td>
                  <td>
                    <span className="text-muted">
                      {template.description || 'No description'}
                    </span>
                  </td>
                  <td>
                    <Badge bg="info">
                      {template.exercise_count || 0} exercise{(template.exercise_count || 0) !== 1 ? 's' : ''}
                    </Badge>
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleStartFromTemplate(template)}
                        title="Start workout from this template"
                      >
                        <i className="fas fa-play me-1"></i>
                        Start
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        title="Edit template"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant={deleteConfirm === template.id ? 'danger' : 'outline-danger'}
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        title={deleteConfirm === template.id ? 'Click again to confirm' : 'Delete template'}
                      >
                        <i className={`fas ${deleteConfirm === template.id ? 'fa-exclamation-triangle' : 'fa-trash'}`}></i>
                        {deleteConfirm === template.id && <span className="ms-1">Sure?</span>}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {showForm && (
        <TemplateForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setEditingTemplate(null);
            setError(null);
          }}
          onSave={handleSave}
          template={editingTemplate}
          error={error}
        />
      )}
    </div>
  );
}

export default TemplateManager;
